import React from 'react';
import styled from 'styled-components';
import {Typography} from '../typography';
import {BgImage} from '../images/BgImage';
import {GridContainer, CenteredGrid} from '../grid/GridContainer';
import Grid from '@material-ui/core/Grid';
import {VerticalSpacer} from '../spacer';
import {Eyebrow} from '../eyebrow';
import {SubnavItemNumber} from '../subnav';
import {AccentColorOptions, useBreakpointUp} from 'epic-ue-theme';
import {AnimateOnViewWrap} from '../../wraps/animation';
import {Button, CmsButtonProps} from '../buttons/Button';

const Styled = styled.div`
    text-align: center;
    position: relative;
    width: 100%;

    .ue-centered-grid {
        height: 100%;
    }

    .attribution-caption .paragraph {
        color: ${(p) => p.theme.palette.text.secondary};
        font-size: 10px;
        line-height: 16px;
        letter-spacing: 0.2px;
    }
`;

const FadedBackgroundImage = styled.div<{imageOpacity?: number}>`
    .background-image {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        height: 100%;
        opacity: ${(p) => p.imageOpacity || 0.25};
    }

    .gradient {
        background: linear-gradient(
            0deg,
            ${(p) => p.theme.palette.background.default} 0%,
            rgba(13, 16, 35, 0) 100%
        );
        position: absolute;
        bottom: 0;
        height: 33%;
        width: 100%;
    }
`;

const Content = styled.div`
    position: relative;
    z-index: 2;
    margin: auto auto;

    h4 {
        display: inline-block;
    }

    .item-number {
        letter-spacing: 3px;
    }
`;

export interface SectionIntroProps {
    title?: string;
    imageSrc?: string;
    caption?: string;
    index?: number | null;
    sectionName?: string;
    accentColor?: AccentColorOptions;
    description?: string;
    button?: CmsButtonProps;
    imageOpacity?: number;
    bgPosY?: 'top' | 'bottom' | 'center' | 'inherit' | 'initial' | 'unset';
    hideBottomPadding?: boolean;
}

export const SectionIntro = ({
    title = '',
    imageSrc = '',
    caption,
    accentColor,
    index,
    sectionName,
    description,
    button = {},
    imageOpacity,
    bgPosY,
    hideBottomPadding = false
}: SectionIntroProps): JSX.Element | null => {
    const mdUp = useBreakpointUp('md');
    return (
        <Styled className="ue-section-intro">
            <FadedBackgroundImage imageOpacity={imageOpacity}>
                <BgImage img={imageSrc} bgPosY={bgPosY} />
                <div className="gradient" />
            </FadedBackgroundImage>
            <Content>
                <VerticalSpacer variant="layout" />
                <GridContainer>
                    <CenteredGrid justifyContent="center">
                        {mdUp ? <Grid item md={2} /> : ''}
                        <Grid item xs={12} md={8}>
                            {sectionName && (
                                <>
                                    <AnimateOnViewWrap translateY={30}>
                                        <Eyebrow>
                                            <div className="section-name">
                                                <SubnavItemNumber big index={index} />
                                                <Typography variant="h4" big color={accentColor}>
                                                    {sectionName}
                                                </Typography>
                                            </div>
                                        </Eyebrow>
                                    </AnimateOnViewWrap>
                                    <VerticalSpacer variant="heading" />
                                </>
                            )}
                            <AnimateOnViewWrap translateY={30}>
                                {title && (
                                    <Typography variant="h2" big>
                                        {title}
                                    </Typography>
                                )}
                                {description && (
                                    <>
                                        <VerticalSpacer variant="custom" spacing={2} />
                                        <Typography variant="p">{description}</Typography>
                                    </>
                                )}
                                {button.label && button.href && (
                                    <>
                                        <VerticalSpacer variant="custom" spacing={4} />
                                        <Button
                                            type="secondary"
                                            href={button.href}
                                            color={accentColor}>
                                            {button.label}
                                        </Button>
                                    </>
                                )}
                                {caption && (
                                    <div className="attribution-caption">
                                        <VerticalSpacer variant="heading" />
                                        <Typography variant="p" caption>
                                            {caption}
                                        </Typography>
                                    </div>
                                )}
                            </AnimateOnViewWrap>
                        </Grid>
                        {mdUp ? <Grid item md={2} /> : ''}
                    </CenteredGrid>
                </GridContainer>
                {!hideBottomPadding && <VerticalSpacer variant="layout" />}
            </Content>
        </Styled>
    );
};
